import { ErrorSection } from '@pickleballinc/features/src/error-pages/error-section';
import Loader from '@pickleballinc/features/src/Loader/Loader';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import { AnimatePresence, motion } from 'framer-motion';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useGetMatchInfos } from '@/hooks/results/hooks';

interface InfoModalProps {
  isOpen?: boolean;
  isSuperAdmin: boolean;
  matchUuid: string;
  onClose: React.MouseEventHandler;
}

export function InfoModal({ isOpen, onClose, isSuperAdmin, matchUuid }: InfoModalProps) {
  const searchParams = useSearchParams();
  const date = searchParams.get('dt') || '';
  const eventId = searchParams.get('eid') || '';
  const { data: matchesResults, isFetching: isFetchingMatches } = useGetMatchInfos(
    [eventId],
    date,
    !!isOpen,
    isSuperAdmin
  );

  const matches = matchesResults?.data ?? [];
  const matchesError = matchesResults?.error;

  const selectedMatch = matches.find((match) => match.match_uuid === matchUuid);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 767);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 767);
    };

    window.addEventListener('resize', handleResize);

    // Set initial state
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }

    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isOpen]);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.1 } },
  };

  const contentVariants = {
    hidden: { y: '-50%', opacity: 0 },
    visible: {
      y: isDesktop ? '-10%' : '20%',
      opacity: 1,
      transition: { duration: 0.1 },
    },
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial='hidden'
          animate='visible'
          exit='hidden'
          variants={backdropVariants}
          className='fixed inset-0 z-[60]'
          onClick={onClose}
          aria-labelledby='modal-title'
          role='dialog'
          aria-modal='true'
        >
          <div className='flex max-h-[550px] items-end justify-center px-2 pb-20 pt-4 text-center sm:block sm:h-[700px] sm:p-0'>
            <div
              className='bg-opacity/75 fixed inset-0 bg-[rgba(0,0,0,0.5)] transition-opacity'
              aria-hidden='true'
            />
            <span className='hidden sm:inline-block sm:h-screen sm:align-middle' aria-hidden='true'>
              &#8203;
            </span>
            <motion.div
              initial='hidden'
              animate='visible'
              exit='hidden'
              variants={contentVariants}
              className='inline-block min-w-full overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all sm:min-w-[600px] sm:max-w-lg sm:align-middle'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='relative bg-gray-50 px-4 py-2 text-center sm:px-6'>
                <div className='sm:flex sm:items-start'>
                  <h3
                    className='w-full text-lg font-medium leading-6 text-gray-900'
                    id='modal-title'
                  >
                    Match
                  </h3>
                </div>
                <button
                  type='button'
                  className=' absolute right-3 top-1 cursor-pointer rounded-md px-4 py-1 text-sm font-medium text-gray-700'
                  onClick={onClose}
                >
                  x
                </button>
              </div>
              <div className='h-[500px] grow overflow-y-auto bg-white px-4 pb-4 pt-5 sm:h-[600px] sm:p-6 sm:pb-4'>
                {isFetchingMatches ? (
                  <Loader isComponent containerClassName='h-full' />
                ) : matchesError ? (
                  <div className='flex items-center h-full'>
                    <ErrorSection title={matchesError} />
                  </div>
                ) : selectedMatch ? (
                  <div className='mt-2 h-full text-xs'>
                    <pre>{JSON.stringify(selectedMatch, null, 2)}</pre>
                  </div>
                ) : (
                  <div className='flex items-center h-full'>
                    <NoDataFound title='Match not found.' />
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
